// ============================================================================
// HelpHint.tsx — УНИВЕРСАЛЬНЫЙ механизм помощи (везде, где нужна подсказка):
//   placement='below' — текст ПОД элементом (поле/блок), не мешает работе;
//   placement='popup'  — кнопка «?» справа; по нажатию — ОКОШКО-ПОПАП НАД
//                        элементом (fixed, не обрезается скролл-контейнерами),
//                        закрывается кликом ВНЕ попапа / Esc / крестиком.
// Тексты — максимально простые, понятные новичку («для тех, кто не видел
// систему и 1С»). SSoT — код (детерминированно).
// ВНИМАНИЕ: все хуки объявлены ДО любых ранних return (правила React).
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

interface HelpHintProps {
  text: string;
  placement?: 'below' | 'popup';
  /** Подпись под кнопкой «?» (если нужна) — не обязательно */
  title?: string;
  className?: string;
}

export function HelpHint({ text, placement = 'popup', title, className }: HelpHintProps) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; below: boolean } | null>(null);

  const close = useCallback(() => setOpen(false), []);

  // Закрытие кликом ВНЕ (кнопка «?» и попап — «внутри»), Esc, скролл окна
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t)) return; // клик по кнопке «?» — переключатель
      if (popupRef.current?.contains(t)) return; // клик ВНУТРИ попапа — не закрываем
      close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  // Позиция попапа: НАД кнопкой (не влезает — ПОД ней); fixed — не обрезается
  useEffect(() => {
    if (!open) { setPos(null); return; }
    const btn = btnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const pw = 280;
    const left = Math.max(8, Math.min(r.right - pw, window.innerWidth - pw - 8));
    const below = r.top - 260 < 8; // попап не влезает над кнопкой
    const top = below ? r.bottom + 12 : r.top - 260;
    setPos({ top, left, below });
  }, [open]);

  if (placement === 'below') {
    return (
      <div className={className}>
        {title && <div className="text-xs font-medium text-muted-foreground">{title}</div>}
        <div className="text-[10px] text-muted-foreground leading-snug">{text}</div>
      </div>
    );
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        title={title || 'Подсказка'}
        aria-label={title || 'Подсказка'}
        onClick={() => setOpen((v) => !v)}
        className={`h-4 w-4 shrink-0 rounded-full border text-[10px] leading-none flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent ${className || ''}`}
      >
        ?
      </button>
      {open && (
        <div
          ref={popupRef}
          className="fixed z-[13000] w-[280px] max-h-[240px] overflow-y-auto rounded-lg border bg-popover text-popover-foreground shadow-xl p-3"
          style={{ top: pos?.top ?? 0, left: pos?.left ?? 0 }}
          role="tooltip"
        >
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0 text-xs leading-relaxed whitespace-pre-wrap">{text}</div>
            <button type="button" onClick={close} className="p-0.5 text-muted-foreground hover:text-foreground shrink-0" aria-label="Закрыть подсказку">
              <X className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================================
// PageHelp — подсказка для целой СТРАНИЦЫ/ПАНЕЛИ: заголовок + кнопка «?»
// справа (попап). Заголовок и текст — как аргументы (SRP).
// ============================================================================

export function PageHelp({ title, text, className }: { title: string; text: string; className?: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 min-w-0 ${className || ''}`}>
      <span className="truncate">{title}</span>
      <HelpHint text={text} />
    </span>
  );
}
